import type { RadioMessage } from '../domain/model';

export type RadioVoice = Pick<SpeechSynthesisVoice, 'name' | 'voiceURI' | 'lang' | 'localService'>;
export type VoiceGender = 'female' | 'male';
export type VoiceSettings = {
  voiceMode: 'mixed' | 'single';
  voiceURI: string;
  femaleVoiceURI: string;
  maleVoiceURI: string;
  voiceSeed: number;
  rate: number;
};

export const isGermanVoice = (voice: RadioVoice) => /^de(?:-|_|$)/i.test(voice.lang);

// Web Speech has no gender/quality fields. Recognize known voice names only;
// unrecognized voices remain unclassified and can be assigned manually in the UI.
export function voiceGender(voice: RadioVoice): VoiceGender | undefined {
  const name = `${voice.name} ${voice.voiceURI}`;
  if (
    /\b(Anna|Helena|Petra|Katja|Hedda|Marlene|Vicki|Klara|Amala|Seraphina|Flo|Sandy|Shelley|Grandma)\b/i.test(
      name,
    )
  )
    return 'female';
  if (
    /\b(Markus|Yannick|Stefan|Hans|Conrad|Florian|Jonas|Ralf|Bernd|Klaus|Thomas|Christoph|Andreas|Killian|Eddy|Reed|Rocko|Grandpa)\b/i.test(
      name,
    )
  )
    return 'male';
  return undefined;
}
export function voiceQuality(voice: RadioVoice) {
  const name = `${voice.name} ${voice.voiceURI}`;
  let score = 30;
  if (/premium/i.test(name)) score = 100;
  else if (/neural|natural|neuron|natürlich/i.test(name)) score = 95;
  else if (/enhanced|erweitert|siri/i.test(name)) score = 85;
  if (/compact|kompakt|espeak|mbrola/i.test(name)) score -= 25;
  if (/\b(Eddy|Flo)\b/i.test(name)) score -= 10;
  if (/\b(Rocko|Reed|Sandy|Shelley)\b/i.test(name)) score -= 20;
  if (/grandma|grandpa|grandmother|grandfather|whisper|zarvox|trinoids/i.test(name)) score -= 100;
  return score + (/^de[-_]DE$/i.test(voice.lang) ? 3 : 0) + (voice.localService ? 1 : 0);
}
export function germanVoices<T extends RadioVoice>(voices: T[]): T[] {
  const unique = [...new Map(voices.filter(isGermanVoice).map((v) => [v.voiceURI, v])).values()];
  return unique.sort(
    (a, b) => voiceQuality(b) - voiceQuality(a) || a.name.localeCompare(b.name, 'de'),
  );
}
export function voicePools<T extends RadioVoice>(voices: T[], settings: VoiceSettings) {
  const all = germanVoices(voices);
  const pool = (gender: VoiceGender, override: string) => {
    const selected = all.find((v) => v.voiceURI === override);
    if (selected) return [selected];
    const candidates = all.filter((v) => voiceGender(v) === gender);
    // Avoid mixing a compact version back in when an enhanced voice is available.
    return candidates.filter((v) => voiceQuality(v) >= voiceQuality(candidates[0]) - 8);
  };
  return {
    all,
    female: pool('female', settings.femaleVoiceURI),
    male: pool('male', settings.maleVoiceURI),
  };
}
export function voiceHash(text: string) {
  let n = 2166136261;
  for (let i = 0; i < text.length; i++) n = Math.imul(n ^ text.charCodeAt(i), 16777619);
  return n >>> 0;
}
export function radioSpeakerKey(message: RadioMessage, sender: string) {
  if (/^Leitstelle\b/i.test(sender)) return `control:${sender}`;
  if (/^Notruf\b/i.test(sender)) return `call:${message.incidentId ?? message.id}`;
  return message.vehicleId ? `vehicle:${message.vehicleId}` : `sender:${sender}`;
}
export function selectRadioVoice<T extends RadioVoice>(
  voices: T[],
  settings: VoiceSettings,
  speaker: string,
  previewGender?: VoiceGender,
) {
  const pools = voicePools(voices, settings);
  const identity = voiceHash(`${settings.voiceSeed}:${speaker}`);
  const gender = previewGender ?? ((identity & 1) === 0 ? 'female' : 'male');
  const chosen = pools[gender];
  const fallback = pools.all.filter((v) => voiceQuality(v) >= voiceQuality(pools.all[0]) - 8);
  const pool = chosen.length ? chosen : fallback;
  const voice =
    settings.voiceMode === 'single'
      ? (pools.all.find((v) => v.voiceURI === settings.voiceURI) ?? pools.all[0])
      : pool.length
        ? pool[voiceHash(`voice:${identity}`) % pool.length]
        : undefined;
  // A small, stable tempo difference per speaker; never fake gender by pitch-shifting.
  const variation =
    settings.voiceMode === 'mixed' ? ((voiceHash(`pace:${identity}`) % 5) - 2) * 0.01 : 0;
  return {
    voice,
    rate: Number(Math.max(0.8, Math.min(1.3, settings.rate + variation)).toFixed(2)),
    pitch: 1,
  };
}
export function voiceAvailability(voices: RadioVoice[], settings: VoiceSettings) {
  const pools = voicePools(voices, settings);
  if (!pools.all.length)
    return 'Keine deutsche Stimme verfügbar. Eine deutsche Systemstimme hinzufügen oder einen anderen Browser nutzen.';
  if (settings.voiceMode === 'single') return 'Eine feste Stimme für alle Funkmeldungen.';
  if (!pools.female.length || !pools.male.length)
    return 'Die automatische Mischung erkennt noch nicht beide Stimmlagen. Ordne unten passende Stimmen zu oder ergänze eine deutsche Systemstimme. Bis dahin werden die verfügbaren Stimmen genutzt.';
  if (pools.female[0]?.voiceURI === pools.male[0]?.voiceURI)
    return 'Für beide Rollen ist dieselbe Stimme gewählt. Wähle zwei verschiedene Stimmen für die Mischung.';
  return 'Männliche und weibliche Stimmen werden zufällig verteilt. Jedes Fahrzeug und die Leitstelle behalten ihre Stimme.';
}

export function neuralGender(seed: number, speaker: string): VoiceGender {
  return (voiceHash(`${seed}:${speaker}`) & 1) === 0 ? 'female' : 'male';
}
